import { useState } from 'react';

export default function AuditSample() {
  const [activeTab, setActiveTab] = useState(0);

  const samples = [
    {
      title: '執行者 DNA',
      subtitle: '底層出廠設定 · 核心執行框架分析',
      content: '透過出生時間與地理座標的精確運算，識別你的核心執行模式。這不是人格測試——這是對結構性能量分佈的數學映射。每一個參數均來自確定性算法，不依賴任何主觀評估。',
      metrics: [
        { label: '決策速度指數', value: '87/100', status: 'optimal' },
        { label: '風險容忍閾值', value: '72/100', status: 'moderate' },
        { label: '團隊協同係數', value: '91/100', status: 'optimal' },
        { label: '創新驅動因子', value: '65/100', status: 'moderate' },
      ],
    },
    {
      title: '時間熱力圖',
      subtitle: '[目標年度] 宏觀動態分析',
      content: '針對目標財年的預測性能量波動分析。紅色區域代表系統壓力峰值；綠色區域代表最優執行窗口。這是天文週期與個體參數交叉運算的結果，非主觀判斷。',
      metrics: [
        { label: 'Q1 目標年度', value: '高壓力相位', status: 'warning' },
        { label: 'Q2 目標年度', value: '穩定化相位', status: 'optimal' },
        { label: 'Q3 目標年度', value: '突破相位', status: 'optimal' },
        { label: 'Q4 目標年度', value: '整合相位', status: 'moderate' },
      ],
    },
    {
      title: '系統過熱預警',
      subtitle: '多重壓力參數同步觸發識別',
      content: '當多個壓力參數同步觸發時，系統進入「過熱」狀態。我們提前識別這些關鍵窗口，為你提供充足的時間重新校準戰略部署，而非在危機發生後被動應對。',
      metrics: [
        { label: '目標年度 Q1 末段', value: '高風險窗口', status: 'warning' },
        { label: '目標年度 Q3 初段', value: '中等風險', status: 'moderate' },
        { label: '目標年度 Q4 初段', value: '低風險', status: 'optimal' },
        { label: '年度聚合風險', value: '在容忍範圍內', status: 'optimal' },
      ],
    },
  ];

  const statusMap: Record<string, { label: string; cls: string }> = {
    optimal: { label: '最優', cls: 'bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20' },
    warning: { label: '預警', cls: 'bg-[#FF4D4D]/10 text-[#FF4D4D] border border-[#FF4D4D]/20' },
    moderate: { label: '中等', cls: 'bg-[#FFD700]/10 text-[#FFD700] border border-[#FFD700]/20' },
  };

  return (
    <section id="sample" className="py-32 px-6 bg-[#050508] border-t border-white/5">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-20">
          <div className="text-[10px] tracking-widest text-[#00D9FF]/60 mb-4 font-mono">
            // REPORT_PREVIEW
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">戰略報告預覽</h2>
          <p className="text-base text-white/40 max-w-2xl mx-auto leading-relaxed">
            以下為 TWINODE 報告的核心模組示例。每份報告均具唯一性——因為每個個體的參數集截然不同。
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {samples.map((sample, index) => (
            <button
              key={index}
              onClick={() => setActiveTab(index)}
              className={`px-5 py-2.5 text-sm font-mono font-bold transition-all whitespace-nowrap cursor-pointer rounded-sm border ${
                activeTab === index
                  ? 'bg-[#00D9FF]/10 text-[#00D9FF] border-[#00D9FF]/40'
                  : 'bg-white/[0.02] text-white/40 border-white/10 hover:border-white/20 hover:text-white/60'
              }`}
            >
              {sample.title}
            </button>
          ))}
        </div>

        {/* Content Display */}
        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div className="space-y-6">
            <div>
              <h3 className="text-2xl font-bold mb-1 text-white">{samples[activeTab].title}</h3>
              <div className="text-xs text-[#00D9FF]/50 mb-5 font-mono tracking-wider">{samples[activeTab].subtitle}</div>
              <p className="text-sm text-white/50 leading-relaxed">
                {samples[activeTab].content}
              </p>
            </div>

            <div className="bg-white/[0.02] p-6 rounded-sm border border-white/8">
              <div className="text-[10px] tracking-widest text-white/25 mb-4 font-mono">// KEY_INSIGHTS</div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-[#00D9FF] mt-0.5 flex-shrink-0">
                    <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                  </span>
                  <span className="text-sm text-white/50">基於真太陽時校正進行運算</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00D9FF] mt-0.5 flex-shrink-0">
                    <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                  </span>
                  <span className="text-sm text-white/50">地理座標精確至小數點後四位</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-[#00D9FF] mt-0.5 flex-shrink-0">
                    <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                  </span>
                  <span className="text-sm text-white/50">麥肯錫級戰略商業詞彙輸出</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Mock Report Display */}
          <div
            className="bg-[#0a0a0f] text-white p-8 rounded-sm border border-white/10"
            role="img"
            aria-label={`TWINODE ${samples[activeTab].title} 戰略報告樣本，顯示指標與置信度`}
          >
            <div className="flex items-center justify-between mb-8 pb-5 border-b border-white/10">
              <div>
                <div className="text-[10px] text-white/30 mb-1 font-mono tracking-widest">TWINODE_REPORT</div>
                <div className="font-bold text-white">[目標年度] 宏觀動態分析</div>
              </div>
              <div className="text-[10px] text-white/25 font-mono">Page 1/24</div>
            </div>

            <div className="space-y-5">
              {samples[activeTab].metrics.map((metric, index) => (
                <div key={index} className="border-l-2 border-[#00D9FF]/20 pl-4">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-xs text-white/40 font-mono">{metric.label}</div>
                    <div className={`text-[10px] px-2 py-0.5 rounded font-mono ${statusMap[metric.status].cls}`}>
                      {statusMap[metric.status].label}
                    </div>
                  </div>
                  <div className="font-mono text-base font-bold text-white">{metric.value}</div>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-5 border-t border-white/10">
              <div className="text-[10px] text-white/25 mb-2 font-mono tracking-widest">CONFIDENCE_LEVEL</div>
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-white/10 h-1.5 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: '94%', backgroundColor: '#00D9FF', boxShadow: '0 0 8px #00D9FF50' }}
                  ></div>
                </div>
                <div className="font-bold font-mono text-[#00D9FF] text-sm">94%</div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-white/[0.02] border border-white/8 px-8 py-6 rounded-sm">
            <div className="text-sm text-white/40 mb-4 leading-relaxed">
              完整報告涵蓋 24 頁深度分析，包含執行者 DNA、時間熱力圖、系統過熱預警等 6 大矩陣模組。
            </div>
            <a
              href="#hero"
              className="inline-block bg-[#00D9FF]/10 border border-[#00D9FF]/30 text-[#00D9FF] px-6 py-3 rounded-sm text-sm font-mono font-bold hover:bg-[#00D9FF]/20 transition-colors whitespace-nowrap cursor-pointer"
            >
              申請 V4.0 Beta 邀請碼，獲取完整報告
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
